import { useState, useEffect } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "@/components/ui/Sidebar";
import Header from "@/components/ui/Header";
import { CommandPalette } from "@/components/CommandPalette";
import { useLogout } from "@/hooks/useLogout";
import { useAuthRefresh } from "@/hooks/useAuthRefresh";
import { RBACProvider, RBACContext } from "@/lib/rbac/context";
import { ScopeProvider } from "@/lib/scope/scope-context";
import { endImpersonation } from "@/lib/api/rbac";
import { useContext } from "react";
import { LogOut } from "lucide-react";

// ─── Impersonation Banner ─────────────────────────────────────────────────────

function ImpersonationBanner() {
  const { refetch } = useContext(RBACContext);
  const isImpersonating = !!localStorage.getItem(
    "impersonation_original_access_token"
  );
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating) return null;

  const impersonatedUser = localStorage.getItem("user");
  let username = "Unknown";
  try {
    if (impersonatedUser) {
      username = JSON.parse(impersonatedUser).username || "Unknown";
    }
  } catch {
    // ignore
  }

  const handleExit = async () => {
    setExiting(true);
    try {
      await endImpersonation();
    } catch {
      // Best-effort audit log
    }

    // Restore original tokens
    const origAccess = localStorage.getItem(
      "impersonation_original_access_token"
    );
    const origRefresh = localStorage.getItem(
      "impersonation_original_refresh_token"
    );
    const origUser = localStorage.getItem("impersonation_original_user");

    if (origAccess) localStorage.setItem("access_token", origAccess);
    if (origRefresh) localStorage.setItem("refresh_token", origRefresh);
    if (origUser) localStorage.setItem("user", origUser);

    localStorage.removeItem("impersonation_original_access_token");
    localStorage.removeItem("impersonation_original_refresh_token");
    localStorage.removeItem("impersonation_original_user");

    await refetch();
    setExiting(false);
    window.location.reload();
  };

  return (
    <div role="status" className="bg-warning text-warning-foreground px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm font-medium">
      <span>
        Viewing as <strong>{username}</strong> — permissions are applied as this
        user
      </span>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="flex items-center gap-1.5 bg-black/20 text-warning-foreground px-3 py-1 rounded-md text-xs font-semibold hover:bg-black/30 transition-colors disabled:opacity-50"
      >
        <LogOut className="h-3.5 w-3.5" />
        {exiting ? "Switching back..." : "Exit Impersonation"}
      </button>
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();

  // Check authentication
  const token = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");

  // Proactive token refresh
  useAuthRefresh();

  // Listen for session expiry events
  useEffect(() => {
    const handler = () => navigate("/login", { replace: true });
    window.addEventListener("auth:session-expired", handler);
    return () => window.removeEventListener("auth:session-expired", handler);
  }, [navigate]);

  // Redirect to login if not authenticated
  if (!token && !refreshToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => navigate("/login", { replace: true }),
      onError: () => navigate("/login", { replace: true }),
    });
  };

  return (
    <RBACProvider>
      <ScopeProvider>
      <div className="flex min-h-screen">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main content */}
        <main className="flex-1 flex flex-col min-w-0 bg-muted/40">
          <ImpersonationBanner />
          <Header
            toggleSidebar={toggleSidebar}
            onLogout={handleLogout}
            onOpenSearch={() => setPaletteOpen(true)}
          />
          <div className="flex-1 p-4 md:p-6 min-w-0 overflow-hidden">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      </ScopeProvider>
    </RBACProvider>
  );
}
